import { invalidateInventoryMovement } from '@/utils/inventoryQueries'
import { useInventoryCatalogSearch } from '@/hooks/useInventoryCatalogSearch'
import { useState, useRef, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeftIcon, TrendingUp, XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { movimentiApi } from '@/api/magazzino'
import { centsToEuroInput, euroToCents } from '@/utils/euroAmount'

/**
 * Form per carico merce in magazzino.
 * Permette di caricare multipli prodotti (stesso fornitore) o componenti.
 */
export default function CaricoForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [tipoItem, setTipoItem] = useState('prodotto')
  const [itemsSelezionati, setItemsSelezionati] = useState([])
  const [searchItem, setSearchItem] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const [causale, setCausale] = useState('')
  const [categoriaCausale, setCategoriaCausale] = useState('')
  const [note, setNote] = useState('')
  const dropdownRef = useRef(null)

  const { items: itemsFiltrati, isSearching } = useInventoryCatalogSearch(tipoItem, searchItem)

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Mutation carico multiplo
  const caricoMutation = useMutation({
    mutationFn: async (items) => {
      // Esegui carico per ogni item
      const promises = items.map(item => movimentiApi.carico(item))
      return Promise.all(promises)
    },
    onSuccess: async () => {
      await invalidateInventoryMovement(queryClient)
      toast.success('Carico registrato con successo')
      navigate('/magazzino/movimenti')
    },
    onError: (error) => {
      const errorMsg = error.response?.data?.detail || 'Errore durante il carico'
      toast.error(errorMsg)
    },
  })

  // Fornitore selezionato (solo per prodotti)
  const fornitoreSelezionato = tipoItem === 'prodotto' && itemsSelezionati.length > 0 
    ? itemsSelezionati[0].fornitore_id
    : null

  const addItem = (item) => {
    // Verifica stesso fornitore (solo per prodotti)
    if (tipoItem === 'prodotto' && fornitoreSelezionato && item.fornitore_id !== fornitoreSelezionato) {
      toast.error('Puoi caricare solo prodotti dello stesso fornitore')
      return
    }

    // Verifica non già aggiunto
    if (itemsSelezionati.find(i => i.id === item.id)) {
      toast.error(`${tipoItem === 'prodotto' ? 'Prodotto' : 'Componente'} già aggiunto`)
      return
    }

    const costo = tipoItem === 'prodotto' ? item.prezzo_acquisto_cents : item.costo_cents
    setItemsSelezionati([...itemsSelezionati, {
      ...item, quantita: 1, costo_unitario_euro: centsToEuroInput(costo),
    }])
    setSearchItem('')
    setShowDropdown(false)
  }

  const removeItem = (id) => {
    setItemsSelezionati(itemsSelezionati.filter(i => i.id !== id))
  }

  const updateQuantita = (id, quantita) => {
    setItemsSelezionati(itemsSelezionati.map(i => 
      i.id === id ? { ...i, quantita: parseInt(quantita) || 1 } : i
    ))
  }

  const updateCosto = (id, costo) => {
    setItemsSelezionati(itemsSelezionati.map(item =>
      item.id === id ? { ...item, costo_unitario_euro: costo } : item
    ))
  }

  const changeTipo = (newTipo) => {
    setTipoItem(newTipo)
    if (newTipo === 'box' && categoriaCausale === 'acquisto') setCategoriaCausale('')
    setItemsSelezionati([])
    setSearchItem('')
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (itemsSelezionati.length === 0) {
      toast.error('Aggiungi almeno un articolo')
      return
    }

    if (!causale.trim()) {
      toast.error('Inserisci la causale del carico')
      return
    }
    if (!categoriaCausale) {
      toast.error('Seleziona il motivo del carico')
      return
    }
    if (categoriaCausale === 'acquisto' && tipoItem !== 'box' &&
        itemsSelezionati.some(item => euroToCents(item.costo_unitario_euro) === null)) {
      toast.error('Inserisci un costo unitario valido per ogni articolo acquistato')
      return
    }

    // Crea array di movimenti
    const movimenti = itemsSelezionati.map(item => {
      const movimento = {
        quantita: item.quantita,
        causale: causale.trim(),
        categoria_causale: categoriaCausale,
        note: note.trim(),
      }
      if (categoriaCausale === 'acquisto' && tipoItem !== 'box') {
        movimento.costo_unitario_cents = euroToCents(item.costo_unitario_euro)
      }
      
      if (tipoItem === 'prodotto') {
        movimento.prodotto = item.id
      } else if (tipoItem === 'box') {
        movimento.box = item.id
      } else {
        movimento.componente = item.id
      }
      
      return movimento
    })

    caricoMutation.mutate(movimenti)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Carico Merce</h1>
          <p className="text-muted-foreground">
            Registra un carico di prodotti, componenti o box assemblate
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Dati Carico
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Tipo Item */}
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={tipoItem === 'prodotto' ? 'default' : 'outline'}
                  onClick={() => changeTipo('prodotto')}
                  className="flex-1"
                >
                  Prodotti
                </Button>
                <Button
                  type="button"
                  variant={tipoItem === 'componente' ? 'default' : 'outline'}
                  onClick={() => changeTipo('componente')}
                  className="flex-1"
                >
                  Componenti
                </Button>
                <Button
                  type="button"
                  variant={tipoItem === 'box' ? 'default' : 'outline'}
                  onClick={() => changeTipo('box')}
                  className="flex-1"
                >
                  Box assemblate
                </Button>
              </div>
            </div>

            {/* Autocomplete Items */}
            <div className="space-y-2">
              <Label htmlFor="search-item">
                Cerca e aggiungi {tipoItem === 'prodotto' ? 'prodotti' : (tipoItem === 'box' ? 'box assemblate' : 'componenti')} *
              </Label>
              <div className="relative" ref={dropdownRef}>
                <Input
                  id="search-item"
                  value={searchItem}
                  onChange={(e) => {
                    setSearchItem(e.target.value)
                    setShowDropdown(true)
                  }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={`Digita nome o codice ${tipoItem === 'prodotto' ? 'prodotto' : (tipoItem === 'box' ? 'box' : 'componente')}...`}
                />
                {showDropdown && searchItem && isSearching && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                    Ricerca in corso...
                  </div>
                )}
                {showDropdown && searchItem && !isSearching && itemsFiltrati.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                    {itemsFiltrati.slice(0, 10).map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => addItem(item)}
                        className="w-full px-4 py-2 text-left hover:bg-accent flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium">{item.nome}</div>
                          <div className="text-sm text-muted-foreground">{item.codice}</div>
                        </div>
                        <Badge variant="outline">
                          {tipoItem === 'prodotto' ? (item.fornitore_nome || 'N/A') : (tipoItem === 'box' ? 'Box assemblata' : item.tipo_display)}
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}
                {showDropdown && searchItem && !isSearching && itemsFiltrati.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg p-4 text-center text-muted-foreground">
                    Nessun articolo trovato
                  </div>
                )}
              </div>
              {tipoItem === 'prodotto' && fornitoreSelezionato && (
                <p className="text-sm text-muted-foreground">
                  ℹ️ Puoi aggiungere solo prodotti dello stesso fornitore
                </p>
              )}
            </div>

            {/* Tabella Items Selezionati */}
            {itemsSelezionati.length > 0 && (
              <div className="space-y-2">
                <Label>
                  Articoli da caricare ({itemsSelezionati.length})
                </Label>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Codice</TableHead>
                      <TableHead>Nome</TableHead>
                      {tipoItem === 'componente' && <TableHead>Tipo</TableHead>}
                      <TableHead className="w-32">Quantità</TableHead>
                      {categoriaCausale === 'acquisto' && tipoItem !== 'box' && <TableHead className="w-36">Costo unitario (€)</TableHead>}
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itemsSelezionati.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-mono text-sm">{item.codice}</TableCell>
                        <TableCell>{item.nome}</TableCell>
                        {tipoItem === 'componente' && (
                          <TableCell>
                            <Badge variant="outline">{item.tipo_display}</Badge>
                          </TableCell>
                        )}
                        <TableCell>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantita}
                            onChange={(e) => updateQuantita(item.id, e.target.value)}
                            className="w-24"
                          />
                        </TableCell>
                        {categoriaCausale === 'acquisto' && tipoItem !== 'box' && (
                          <TableCell>
                            <Input
                              aria-label={`Costo unitario ${item.nome}`}
                              inputMode="decimal"
                              value={item.costo_unitario_euro}
                              onChange={(e) => updateCosto(item.id, e.target.value)}
                              className="w-28"
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(item.id)}
                            className="text-red-600"
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="categoria-causale-carico">Motivo del carico *</Label>
              <select
                id="categoria-causale-carico"
                value={categoriaCausale}
                onChange={(e) => setCategoriaCausale(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
                required
              >
                <option value="">Seleziona motivo</option>
                {tipoItem !== 'box' && <option value="acquisto">Acquisto</option>}
                <option value="rientro">Rientro / reso</option>
                <option value="rettifica">Rettifica inventario</option>
                <option value="altro">Altro carico</option>
              </select>
              <p className="text-xs text-muted-foreground">Per acquisti, verifica il costo unitario: sarà conservato nello storico.</p>
            </div>

            {/* Causale */}
            <div className="space-y-2">
              <Label htmlFor="causale">Causale *</Label>
              <Input
                id="causale"
                value={causale}
                onChange={(e) => setCausale(e.target.value)}
                placeholder="Es: Acquisto fornitore XYZ"
              />
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label htmlFor="note">Note (opzionale)</Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note aggiuntive..."
                rows={3}
              />
            </div>

            {/* Azioni */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={caricoMutation.isPending}
              >
                {caricoMutation.isPending ? 'Registrazione...' : 'Registra Carico'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  )
}
